import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  secondImageOfListingTemplateInput,
  thirdImageOfListingTemplateInput,
  includeBsadInput,
  listingPriceOptional,
  mainListingInput,
  mainTemplateColorInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const modern_showcase_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.modernShowcase.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-modern-showcase-1.jpg",
  description: i18n.t("creativesGenerator.templates.modernShowcase.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    thirdImageOfListingTemplateInput(false), // not required for this template
    listingPriceOptional,
    {
      label: i18n.t("creativesGenerator.templates.modernShowcase.headlineText"),
      type: "text",
      nameInPrompt: "headline_text",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.modernShowcase.headlineTextDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t("creativesGenerator.templates.modernShowcase.headlineTextDefaultValue", {
          city: mainListing?.address?.city ?? "_____"
        });
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.modernShowcase.propertyDetails"),
      type: "text",
      nameInPrompt: "property_details",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.modernShowcase.propertyDetailsDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t("creativesGenerator.templates.modernShowcase.propertyDetailsDefaultValue", {
          roomsCount: mainListing?.roomsCount ?? "____",
          neighborhood: mainListing?.address?.neighborhood ?? "____"
        });
      }
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, mainColorName, lighterColorValue, darkerColorValue } =
      getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    const headlineText = textParams.find(
      (param) => param.nameInPrompt === "headline_text"
    )?.finalValue;
    const propertyDetails = textParams.find(
      (param) => param.nameInPrompt === "property_details"
    )?.finalValue;
    const price = textParams.find((param) => param.nameInPrompt === "listing_price")?.finalValue;
    const hasPrice = price && typeof price === "string" && price.trim() !== "";

    const thirdImage = params.templateFinalInputs.find(
      (input) => input.nameInPrompt === "listing_image_3"
    )?.finalValue;
    const hasThirdImage = thirdImage && typeof thirdImage === "string" && thirdImage.trim() !== "";

    return `
{
  "description": "Modern Editorial Real Estate Showcase",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Clean, modern, editorial, premium",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "editorial_color_dominant",
    "description": "This is NOT a photo-background layout. The dominant visual is a rich solid/gradient colored background. Property images sit inside shaped cutouts. Bold typography is the star of the design.",

    "background": {
      "type": "solid_with_subtle_texture",
      "primary_color": "${darkerColorValue}",
      "gradient": "Very subtle radial gradient from ${mainColorValue} (center-top) to ${darkerColorValue} (edges)",
      "texture": "Extremely subtle geometric dot/line pattern at 3-5% opacity for depth — almost invisible but adds premium feel"
    },

    "geometric_accents": {
      "description": "Thin decorative geometric elements that add editorial flair",
      "elements": [
        {
          "type": "thin_line",
          "color": "${lighterColorValue}",
          "opacity": 0.3,
          "position": "Vertical line on the left side, about 8% from the left edge, running from 15% to 75% height"
        },
        {
          "type": "small_circle",
          "color": "${lighterColorValue}",
          "opacity": 0.15,
          "position": "Large circle (about 60% of canvas size), centered at top-right, mostly cropped off-canvas — only the bottom-left arc is visible as a subtle background shape"
        }
      ]
    },

    "sections": [
      {
        "name": "headline_section",
        "position": "Top-left area, about 55% width and 30% height from top",
        "content": [
          {
            "type": "main_headline",
            "text": "${headlineText}",
            "style": {
              "font_color": "white",
              "font_size": "extra_large",
              "font_weight": "extra_bold",
              "text_align": "right",
              "line_height": "very_tight",
              "letter_spacing": "tight"
            },
            "description": "The headline is the dominant text element. Extra-large, bold, white. It should feel like a magazine cover headline — impactful and clean. Right-aligned for Hebrew RTL."
          }
        ]
      },
      {
        "name": "images_section",
        "position": "Right side and center of canvas",
        "content": [
          {
            "type": "primary_image_frame",
            "source": "listing_image",
            "position": "Right side, vertically centered, about 58% width and 62% height of canvas",
            "style": {
              "object_fit": "cover",
              "border_radius": "16px",
              "box_shadow": "large soft shadow (0 8px 32px rgba(0,0,0,0.3)) for floating depth effect"
            },
            "description": "The main property image in a large rounded rectangle frame. It should feel like it's floating above the colored background with a strong drop shadow."
          },
          {
            "type": "secondary_image_frame",
            "source": "listing_image_2",
            "position": "Overlapping the bottom-left corner of the primary image, offset by about 20% to the left and 15% downward",
            "style": {
              "width": "about 42% of canvas width",
              "height": "about 42% of canvas height",
              "object_fit": "cover",
              "border_radius": "16px",
              "border": "4px solid white",
              "box_shadow": "medium soft shadow for depth"
            },
            "description": "A smaller secondary image that overlaps the primary image's bottom-left corner. The white border and shadow make it feel like a photo card layered on top. Creates depth and visual interest."
          }${
            hasThirdImage
              ? `,
          {
            "type": "tertiary_image_frame",
            "source": "listing_image_3",
            "position": "Overlapping the top-left corner of the primary image, offset by about 15% to the left and 10% upward",
            "style": {
              "width": "about 25% of canvas width",
              "height": "about 25% of canvas height",
              "object_fit": "cover",
              "border_radius": "16px",
              "border": "3px solid white",
              "box_shadow": "medium soft shadow for depth"
            },
            "description": "A third smaller image layered near the top-left of the primary image. Together with the secondary image, it creates a cascading stack of photos. Slightly smaller than the secondary image to maintain visual hierarchy."
          }`
              : ""
          }
        ]
      },
      {
        "name": "details_section",
        "position": "Bottom-left area, below the headline, about 50% width",
        "content": [
          {
            "type": "property_info",
            "text": "${propertyDetails}",
            "style": {
              "font_color": "${lighterColorValue}",
              "font_size": "medium",
              "font_weight": "regular",
              "text_align": "right"
            }
          },
          ${
            hasPrice
              ? `{
            "type": "price_tag",
            "text": "${price}",
            "style": {
              "font_color": "white",
              "font_size": "very_large",
              "font_weight": "bold",
              "text_align": "right",
              "margin_top": "8px"
            },
            "description": "The price displayed large and bold in white, creating a strong visual anchor in the bottom-left area"
          }`
              : ""
          }
        ]
      }
    ]
  },
  ${
    hasRealtorImage(params)
      ? `${getRealtorImagePrompt(
          params,
          "The realtor should overlap the footer slightly to create depth. The realtor image should be slightly smaller than in other templates (about 25% width) to maintain the clean editorial feel."
        )},`
      : ""
  }
  ${getVerticalFooterPrompt(params)},
  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    "listing_image_2": "${getInputImageLabel("listing_image_2")}",${
      hasThirdImage
        ? `
    "listing_image_3": "${getInputImageLabel("listing_image_3")}",`
        : ""
    }
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "CRITICAL: The background must be the DOMINANT colored surface — NOT a photo background. The color ${mainColorName} must be clearly visible as the main visual element",
    "Property images must appear INSIDE rounded frames with shadows, floating above the background — NOT as full-bleed backgrounds",
    "The headline typography should be magazine-quality: extra bold, tight line-height, impactful",
    "Geometric accent elements should be barely visible — they add texture, not distraction",
    "The overall aesthetic should feel like a premium real estate magazine spread, not a typical social media ad",
    "Maintain generous whitespace (colored space) between elements for a clean, breathable layout",
    "DO NOT alter the provided images beyond removing backgrounds and resizing/cropping as necessary for composition",
    "The property images should overlap each other to create a layered, cascading depth effect"
  ]
}`;
  }
};
